import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { HERO_IMAGES, BRAND } from "../../data/menu";

const line = {
  hidden: { y: "115%" },
  show: (i) => ({
    y: 0,
    transition: { duration: 1.05, ease: [0.22, 1, 0.36, 1], delay: 0.35 + i * 0.12 },
  }),
};

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y1 = useTransform(scrollYProgress, [0, 1], [0, -120]);
  const y2 = useTransform(scrollYProgress, [0, 1], [0, 90]);
  const y3 = useTransform(scrollYProgress, [0, 1], [0, -60]);
  const parallax = [y1, y2, y3];

  return (
    <section ref={ref} id="top" className="relative min-h-[100svh] overflow-hidden pt-28 md:pt-24">
      {/* faint fort watermark */}
      <div className="pointer-events-none absolute -right-24 top-24 hidden lg:block opacity-[0.06]">
        <img src={BRAND.logo} alt="" className="w-[520px]" />
      </div>

      <div className="mx-auto max-w-7xl px-5 md:px-8 grid lg:grid-cols-[1.15fr_0.85fr] gap-10 items-center min-h-[80svh]">
        {/* Left: kinetic type */}
        <div>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2, duration: 0.6 }}
            className="flex items-center gap-3 mb-6"
          >
            <span className="h-px w-10 bg-[#d97706]" />
            <span className="text-xs md:text-sm uppercase tracking-[0.3em] font-semibold text-[#7a1f1f]">
              {BRAND.marathi}
            </span>
          </motion.div>

          <h1 className="font-display font-bold tracking-tighter leading-[0.86] text-[#331818] text-6xl sm:text-7xl md:text-8xl">
            <span className="line-mask"><motion.span variants={line} custom={0} initial="hidden" animate="show" className="block">Authentic</motion.span></span>
            <span className="line-mask"><motion.span variants={line} custom={1} initial="hidden" animate="show" className="block text-[#7a1f1f]">Maharashtrian</motion.span></span>
            <span className="line-mask"><motion.span variants={line} custom={2} initial="hidden" animate="show" className="block italic font-accent text-[#d97706] text-5xl sm:text-6xl md:text-7xl">flavours</motion.span></span>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.95, duration: 0.8 }}
            className="mt-7 max-w-md text-base md:text-lg text-[#5c3a3a] leading-relaxed"
          >
            From our kitchen to your home — every thali, bhakri and rassa is
            slow-cooked with love, the way an Aaji would make it back home.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.1, duration: 0.8 }}
            className="mt-9 flex flex-wrap items-center gap-4"
          >
            <button
              data-testid="hero-explore-menu"
              onClick={() => document.getElementById("menu")?.scrollIntoView({ behavior: "smooth" })}
              className="group inline-flex items-center gap-2 rounded-full bg-[#7a1f1f] px-7 py-3.5 text-sm font-semibold text-[#f7f0e0] transition-colors duration-300 hover:bg-[#d97706]"
            >
              Explore the Menu
              <ArrowDown size={16} className="transition-transform duration-300 group-hover:translate-y-1" />
            </button>
            <a
              data-testid="hero-whatsapp"
              href={`https://wa.me/${BRAND.phoneRaw}`}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 rounded-full border border-[#7a1f1f]/30 px-7 py-3.5 text-sm font-semibold text-[#7a1f1f] transition-colors duration-300 hover:bg-[#7a1f1f] hover:text-[#f7f0e0]"
            >
              Order on WhatsApp
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.3, duration: 1 }}
            className="mt-10 flex items-center gap-6 text-xs uppercase tracking-[0.15em] text-[#5c3a3a]"
          >
            <span>Home Cooked</span>
            <span className="h-1 w-1 rounded-full bg-[#d97706]" />
            <span>Fresh Ingredients</span>
            <span className="h-1 w-1 rounded-full bg-[#d97706]" />
            <span>Hygienic &amp; Safe</span>
          </motion.div>
        </div>

        {/* Right: parallax spotlight dishes */}
        <div className="grid grid-cols-12 grid-rows-2 gap-3 sm:gap-4 h-[420px] sm:h-[520px] lg:h-[600px]">
          {HERO_IMAGES.map((img, i) => {
            const span = [
              "col-span-7 row-span-2",
              "col-span-5 row-span-1",
              "col-span-5 row-span-1",
            ][i];
            return (
              <motion.figure
                key={img.label}
                style={{ y: parallax[i] }}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.6 + i * 0.18, duration: 1, ease: [0.22, 1, 0.36, 1] }}
                className={`relative ${span} overflow-hidden rounded-[22px] ring-1 ring-[#7a1f1f]/15 shadow-[0_20px_60px_rgba(122,31,31,0.15)]`}
              >
                <img src={img.url} alt={img.label} className="h-full w-full object-cover" />
                <figcaption className="absolute bottom-0 inset-x-0 bg-[#2b1414]/85 px-3 py-2 text-[11px] font-medium uppercase tracking-[0.12em] text-[#f7f0e0]">
                  {img.label}
                </figcaption>
              </motion.figure>
            );
          })}
        </div>
      </div>
    </section>
  );
}
